import { getHistory } from '@api/cut';
import { AppRoutes } from '@consts/routes';
import { useFetch } from '@hooks/useFetch';
import { useQuery } from '@hooks/useQuery';
import { useSelector } from 'react-redux';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { BasePage } from './bases/BasePage';
import { BreadcrumbSubBar } from '@components/BreadcrumbSubBar';
import SearchOffIcon from '@mui/icons-material/SearchOff';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { Box, Typography, useMediaQuery, useTheme, Skeleton } from '@mui/material';
import { useAlert } from '@hooks/useAlert';
import { CutTimeline } from '@components/CutHistory/CutTimeline';
import { useLoading } from '@hooks/useLoading';
import { AppShortcut, CloudSync, SwapHoriz } from '@mui/icons-material';
import { colors, typography as typographyTokens, radii, shadows } from '@components/design-system';

import type { RootState } from '@features/redux/store';
import type { ComponentProps } from 'react';

export const CutHistory = () => {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));
    const { IdToken } = useSelector((state: RootState) => state.auth);
    const navigate = useNavigate();
    const params = useQuery();
    const { setAlert } = useAlert();
    const { anyLoading } = useLoading();

    const CutID = params.get('id');
    if (!CutID) {
        setAlert('No Cut ID provided!', 'error');
        navigate(AppRoutes.Activity);
    }

    const { data } = useFetch({
        defaultValue: { items: [], cutId: '', status: 'Unknown' },
        fetcher: () => {
            return getHistory({
                token: IdToken!,
                params: {
                    CutID,
                },
            });
        },
        modifier: (history) => {
            if (!history.length) return { items: [], cutId: '', status: 'Unknown' };

            return {
                status: history[0].status || 'Unknown',
                cutId: history[0].cutId,
                items: [
                    {
                        content: history[0].startedDate,
                        label: 'Cut made',
                        description: 'The cut was performed.',
                        icon: <AppShortcut />,
                        color: 'info' as const,
                    },
                    {
                        content: history[0].createdDate,
                        label: 'Cut synced',
                        description: 'Data about this cut was synced to the cloud.',
                        icon: <CloudSync />,
                        color: 'success' as const,
                    },
                    ...history.reduce((acc, { eventDate, oldStatus, newStatus }) => {
                        if (!eventDate) return acc;

                        acc.push({
                            content: eventDate,
                            label: 'Status change',
                            description: `"${oldStatus || 'Unknown'}" → "${newStatus || 'Unknown'}"`,
                            icon: <SwapHoriz />,
                            color: 'secondary' as const,
                        });

                        return acc;
                    }, [] as ComponentProps<typeof CutTimeline>['items']),
                ],
            };
        },
    });

    return (
        <BasePage>
            {!isMobile && <BreadcrumbSubBar />}

            <Box
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    backgroundColor: colors.neutral[100],
                    p: isMobile ? 2 : 3,
                }}>
                {/* Header */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 3 }}>
                    <Box
                        component={RouterLink}
                        to={AppRoutes.Activity}
                        sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: colors.neutral[500],
                            textDecoration: 'none',
                            transition: 'color 150ms ease',
                            '&:hover': {
                                color: colors.neutral[900],
                            },
                        }}>
                        <ArrowBackIcon sx={{ fontSize: 24 }} />
                    </Box>
                    <Typography
                        sx={{
                            fontSize: isMobile ? typographyTokens.fontSize.xl : typographyTokens.fontSize['2xl'],
                            fontWeight: typographyTokens.fontWeight.bold,
                            color: colors.neutral[900],
                        }}>
                        Cut History
                        {data.cutId && (
                            <Typography
                                component='span'
                                sx={{
                                    fontSize: isMobile ? typographyTokens.fontSize.lg : typographyTokens.fontSize.xl,
                                    fontWeight: typographyTokens.fontWeight.normal,
                                    color: colors.neutral[500],
                                    ml: 1,
                                }}>
                                #{data.cutId}
                            </Typography>
                        )}
                    </Typography>
                </Box>

                {/* Content Card */}
                <Box
                    sx={{
                        backgroundColor: colors.neutral[0],
                        borderRadius: `${radii.lg}px`,
                        border: `1px solid ${colors.neutral[200]}`,
                        boxShadow: shadows.card,
                        p: isMobile ? 2 : 3,
                    }}>
                    {/* Loading State */}
                    {anyLoading && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            <Skeleton variant='text' width={200} height={32} />
                            <Skeleton variant='rectangular' height={200} sx={{ borderRadius: `${radii.md}px` }} />
                        </Box>
                    )}

                    {/* Empty State */}
                    {!anyLoading && !data.items.length && (
                        <Box
                            sx={{
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                justifyContent: 'center',
                                py: 6,
                                gap: 2,
                            }}>
                            <Box
                                sx={{
                                    width: 80,
                                    height: 80,
                                    borderRadius: '50%',
                                    backgroundColor: colors.neutral[100],
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                }}>
                                <SearchOffIcon sx={{ fontSize: 40, color: colors.neutral[400] }} />
                            </Box>
                            <Typography
                                sx={{
                                    fontSize: typographyTokens.fontSize.lg,
                                    fontWeight: typographyTokens.fontWeight.semibold,
                                    color: colors.neutral[700],
                                }}>
                                No History Available
                            </Typography>
                            <Typography
                                sx={{
                                    fontSize: typographyTokens.fontSize.sm,
                                    color: colors.neutral[500],
                                    textAlign: 'center',
                                }}>
                                There is no history currently available for this cut.
                            </Typography>
                        </Box>
                    )}

                    {/* Timeline Content */}
                    {!anyLoading && data.items.length > 0 && (
                        <>
                            {/* Status Badge */}
                            <Box sx={{ mb: 3, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                                <Typography
                                    sx={{
                                        fontSize: typographyTokens.fontSize.sm,
                                        color: colors.neutral[600],
                                    }}>
                                    Current Status:
                                </Typography>
                                <Box
                                    sx={{
                                        px: 1.5,
                                        py: 0.5,
                                        borderRadius: `${radii.full}px`,
                                        backgroundColor: colors.primary[100],
                                        color: colors.primary[700],
                                        fontSize: typographyTokens.fontSize.sm,
                                        fontWeight: typographyTokens.fontWeight.medium,
                                    }}>
                                    {data.status}
                                </Box>
                            </Box>

                            {/* Timeline */}
                            <CutTimeline items={data.items} />
                        </>
                    )}
                </Box>
            </Box>
        </BasePage>
    );
};
